import * as React from "react";
import { Link } from "react-router-dom";
import BackButton from "../../core/BackButton/BackButton";
import ConfirmModal from "../../core/ConfirmModal/ConfirmModal";
import * as Constants from "../../core/constants";
import ErrorModal from "../../core/ErrorModal/ErrorModal";
import Spinner from "../../core/Spinner/Spinner";
import Toast from "../../core/Toast/Toast";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IDictionary from "../../types/IDictionary";
import IResponse from "../../types/IResponse";
import ISendMessageRequest from "../../types/ISendMessageRequest";
import { ISendMessageResponse } from "../../types/ISendMessageResponse";
import RecipientInput from "./RecipientInput";

interface IRecipient {
    value: string;
    countryCode: string;
}

interface ISendMessageProps extends ITranslationProps {
    loading: boolean;
    sendMessage: (request: ISendMessageRequest) => Promise<IResponse<ISendMessageResponse> | null>;
}

interface ISendMessageState {
    validated: boolean;
    messageName: string;
    messageBody: string;
    messageSubject: string;
    messageType: string;
    recipients: IDictionary<IRecipient>;
    showConfirmModal: boolean;
    showErrorModal: boolean;
    errorMessages: string[];
    sentMessageID: number | null;
    sentMessageType: string;
}

class SendMessage extends React.Component<ISendMessageProps, ISendMessageState> {

    private toastElement: Toast | null;
    private lastRecipientID: number = 1;
    private formElement: HTMLFormElement | null;

    constructor(props: ISendMessageProps) {
        super(props);

        this.state = {
            validated: false,
            messageBody: "",
            messageName: "",
            messageSubject: "",
            messageType: "",
            recipients: { "0": { value: "", countryCode: "" } },
            showConfirmModal: false,
            showErrorModal: false,
            errorMessages: [],
            sentMessageID: null,
            sentMessageType: ""
        };

        this.onSendMessageClick = this.onSendMessageClick.bind(this);
        this.onMessageTypeChange = this.onMessageTypeChange.bind(this);
        this.onMessageBodyChange = this.onMessageBodyChange.bind(this);
        this.onMessageNameChange = this.onMessageNameChange.bind(this);
        this.onMessageSubjectChange = this.onMessageSubjectChange.bind(this);
        this.onRecipientChange = this.onRecipientChange.bind(this);
        this.onRecipientDelete = this.onRecipientDelete.bind(this);
        this.closeConfirmModal = this.closeConfirmModal.bind(this);
        this.closeErrorModal = this.closeErrorModal.bind(this);
        this.addRecipient = this.addRecipient.bind(this);
        this.getRecipients = this.getRecipients.bind(this);
        this.sendMessage = this.sendMessage.bind(this);
    }

    public render() {
        const recipientNodes: React.ReactNode[] = [];
        Object.getOwnPropertyNames(this.state.recipients).forEach(name => {
            recipientNodes.push(
                <RecipientInput key={name} id={name}
                    value={this.state.recipients[name].value}
                    countryCode={this.state.recipients[name].countryCode}
                    onChange={this.onRecipientChange}
                    onDelete={this.onRecipientDelete}
                    type={this.state.messageType} />
            );
        });

        return (
            <div>
                {this.props.loading && <Spinner />}
                <BackButton />
                <h2>{this.props.t("SendMessage.Title")}</h2>
                <p>{this.props.t("SendMessage.PageDescription")}</p>

                <form ref={ref => this.formElement = ref} className={this.state.validated ? "was-validated" : "needs-validation"} noValidate={true}>

                    <div className="card mt-3">
                        <div className="card-header">
                            <h5>{this.props.t("SendMessage.MessageInformation")}</h5>
                        </div>
                        <div className="card-body">

                            <div className="form-group">
                                <label>{this.props.t("SendMessage.Name")}</label>
                                <input data-id="message-name" type="text" className="form-control" placeholder={this.props.t("SendMessage.Name")}
                                    value={this.state.messageName} onChange={this.onMessageNameChange}
                                    required={true} maxLength={50} />
                                <div className="invalid-feedback">
                                    {this.props.t("SendMessage.NameInvalid")}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{this.props.t("SendMessage.Body")}</label>

                                <textarea data-id="message-body" className="form-control" placeholder={this.props.t("SendMessage.Body")}
                                    value={this.state.messageBody} onChange={this.onMessageBodyChange} required={true} maxLength={500} />

                                <div className="invalid-feedback">
                                    {this.props.t("SendMessage.BodyInvalid")}
                                </div>
                            </div>

                            {this.state.messageType === Constants.MESSAGE_TYPE_EMAIL &&
                                <div className="form-group">
                                    <label>{this.props.t("SendMessage.Subject")}</label>
                                    <input data-id="message-subject" type="text" className="form-control" placeholder={this.props.t("SendMessage.Subject")}
                                        value={this.state.messageSubject} onChange={this.onMessageSubjectChange} required={true} maxLength={50} />
                                    <div className="invalid-feedback">
                                        {this.props.t("SendMessage.SubjectInvalid")}
                                    </div>
                                </div>
                            }

                            <div className="form-group">
                                <label>{this.props.t("SendMessage.MessageType")}</label>
                                <div className="form-check">
                                    <input data-id="message-type" className="form-check-input" type="radio" name="messageTypeRadios"
                                        id="messageTypeRadioEmail" value={Constants.MESSAGE_TYPE_EMAIL}
                                        required={true} onChange={this.onMessageTypeChange} />
                                    <label className="form-check-label" htmlFor="messageTypeRadioEmail">{this.props.t("Global.MessageTypeEmail")}</label>
                                </div>
                                <div className="form-check">
                                    <input data-id="message-type" className="form-check-input" type="radio" name="messageTypeRadios"
                                        id="messageTypeRadioSMS" value={Constants.MESSAGE_TYPE_SMS} required={true}
                                        onChange={this.onMessageTypeChange} />
                                    <label className="form-check-label" htmlFor="messageTypeRadioSMS">{this.props.t("Global.MessageTypeSMS")}</label>
                                </div>
                                <div className="form-check">
                                    <input data-id="message-type" className="form-check-input" type="radio" name="messageTypeRadios"
                                        id="messageTypeRadioVoice" value={Constants.MESSAGE_TYPE_VOICE} required={true}
                                        onChange={this.onMessageTypeChange} />
                                    <label className="form-check-label" htmlFor="messageTypeRadioVoice">{this.props.t("Global.MessageTypeVoice")}</label>
                                    <div className="invalid-feedback" style={{ marginLeft: "-1.25em" }}>
                                        {this.props.t("SendMessage.MessageTypeInvalid")}
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{this.props.t("SendMessage.Recipients")}</label>
                                {recipientNodes}
                                <div className="invalid-feedback"
                                    style={{ display: this.state.validated && this.getRecipients().length === 0 ? "block" : "none" }}>
                                    {this.props.t("SendMessage.RecipientRequired")}
                                </div>

                                <div>
                                    <button type="button" className="btn btn-secondary mt-2" onClick={this.addRecipient}>{this.props.t("SendMessage.AddRecipient")}</button>
                                </div>
                            </div>

                        </div>
                    </div>

                    <button type="button" data-id="save-button" className="btn btn-primary mt-3 px-4"
                        onClick={this.onSendMessageClick}>{this.props.t("SendMessage.SendMessageButton")}</button>

                    {this.state.sentMessageID &&
                        <Link to={`/message/${this.state.sentMessageID}/${this.state.sentMessageType}`}
                            className="ml-3 mt-3 btn btn-info">{this.props.t("SendMessage.ViewMessage")}</Link>
                    }

                </form>

                <ConfirmModal
                    onRequestClose={this.closeConfirmModal}
                    onConfirmation={this.sendMessage}
                    isOpen={this.state.showConfirmModal}
                    title={this.props.t("Global.Confirm")}
                    message={this.props.t("SendMessage.ConfirmSendMessage")} />

                <ErrorModal
                    title={this.props.t("Global.Error")}
                    errors={this.state.errorMessages}
                    isOpen={this.state.showErrorModal}
                    onRequestClose={this.closeErrorModal} />

                <Toast ref={ref => this.toastElement = ref} />
            </div>
        );
    }

    private onRecipientChange(id: string, value: string, countryCode: string) {
        const recipients = this.state.recipients;
        recipients[id] = { value, countryCode };
        this.setState({ recipients });
    }

    private onRecipientDelete(id: string) {
        const recipients = this.state.recipients;
        delete recipients[id];
        this.setState({ recipients });
    }

    private addRecipient() {
        this.lastRecipientID++;
        const recipients = this.state.recipients;
        recipients[this.lastRecipientID.toString()] = { value: "", countryCode: "" };
        this.setState({ recipients });
    }

    private onMessageNameChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ messageName: event.target.value });
    }

    private onMessageBodyChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({ messageBody: event.target.value });
    }

    private onMessageSubjectChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ messageSubject: event.target.value });
    }

    private onMessageTypeChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ messageType: event.target.value });
    }

    private closeConfirmModal() {
        this.setState({ showConfirmModal: false });
    }

    private closeErrorModal() {
        this.setState({ showErrorModal: false });
    }

    private onSendMessageClick() {
        this.setState({ validated: true });

        if (this.formElement && this.formElement.checkValidity() && this.getRecipients().length > 0) {
            this.setState({ showConfirmModal: true });
        }
    }

    private getRecipients(): string[] {
        const recipients: string[] = [];
        Object.keys(this.state.recipients).forEach(key => {
            recipients.push(this.state.recipients[key].countryCode + this.state.recipients[key].value);
        });
        return recipients;
    }

    private async sendMessage() {

        const request: ISendMessageRequest = {
            messageBody: this.state.messageBody,
            messageName: this.state.messageName,
            messageType: this.state.messageType,
            recipients: this.getRecipients(),
            messageSubject: this.state.messageSubject
        }

        this.setState({ sentMessageID: null });

        const response = await this.props.sendMessage(request);

        if (response) {

            this.lastRecipientID = 1;
            this.setState({
                messageBody: "", 
                recipients: { 0: { value: "", countryCode: "" } },
                messageSubject: "", 
                messageName: ""
            })

            if (response.success) {
                if (this.toastElement) {
                    this.toastElement.success(this.props.t("Global.Success"));
                }
                this.setState({ sentMessageID: response.payload.messageID, sentMessageType: response.payload.messageType });
            }
            else {
                this.setState({ showErrorModal: true, errorMessages: response.errorMessages })
            }
        }

        this.setState({ validated: false });
    }

}

export default translateComponent(SendMessage);



// WEBPACK FOOTER //
// ./src/components/SendMessage/SendMessage.tsx